// =============================================================================
// Optim TURPE v2 — logique de restitution (DEV-715), fonctions pures.
// Qualifie l'état (les 4 états honnêtes), chiffre l'économie ferme, et dérive
// les « réserves » explicites — JAMAIS « fiabilité faible » (on dit « valide
// sous réserve(s) », réserves choisies/assumées par l'utilisateur).
// =============================================================================

import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import { ServiceError, getUserFacingDescription } from "@core/exceptions"
import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { JOURS_POUR_EQUIVALENT_ANNUEL as JOURS_EQUIVALENT_ANNUEL } from "@modules/courtage/utils/optim-turpe-v2.fenetre"
import {
    LIBELLE_DEPASSEMENTS_ESTIMES_SIGLE,
    LIBELLE_NIVEAU_DONNEES as LIBELLE_NIVEAU_DONNEES_REF,
    LIBELLE_POSTE_TURPE,
    LIBELLE_TOTAL_HORS_DEPASSEMENTS,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import type {
    OptimTurpeV2DriverType,
    OptimTurpeV2EtatServi,
    OptimTurpeV2Etude,
    OptimTurpeV2MeilleurePeriode,
    OptimTurpeV2NiveauDonnees,
    OptimTurpeV2PlageKey,
    OptimTurpeV2Reserve,
} from "@modules/courtage/types/optim-turpe-v2"

// =============================================================================
// Ce qui a DISPARU d'ici, et pourquoi (DEV-1913).
//
// - `SEGMENT_FLOOR_KVA` (C4 37, C2 250) et `getSegmentFloorKva` : la décision A6
//   du 21/09/2026 supprime tout minimum de 250 kW en C2 et réduit la contrainte
//   C4 à « au moins une puissance du dénivelé dépasse 36 kVA », que le MOTEUR
//   fait respecter (`exiger_segment_c4`, DEV-1909). Un plancher recopié ici
//   annonçait au client une règle qui n'existe plus (DEV-1860, DEV-1707 :
//   chaque occurrence a demandé un forçage manuel).
// - `QUASI_OPTIMAL_EUR` : le seuil qui sépare « économie » de « déjà optimale »
//   est servi par le moteur (`restitution.etat_seuil_eur`, DEV-1907). Son repli
//   nommé vit dans `constants/optim-turpe-v2.ts`.
// - `isSurSegmente` et l'état `sur_segmente` : remplacés par le contrôle de
//   vraisemblance du dénivelé actuel, servi et rendu sur la ligne de la donnée
//   concernée (`optim-turpe-v2.bornes.ts`, §4.5 du contrat cible).
// =============================================================================

// =============================================================================
// Ce qui a DISPARU d'ici, et pourquoi (revue C4 du 23/09).
//
// `getEconomieAnnuelleEur` CALCULAIT un montant (`-variation_absolue_turpe`,
// sinon `avant − après`, sinon **0**) et servait de repli aux deux registres.
// C'était, dans un lot dont la règle affichée est l'inverse, à la fois un
// montant fabriqué par la surface et un 0 pour une absence : sur une étude qui
// ne sert pas son économie, l'écran et le rapport annonçaient « 0 € » là où la
// donnée manque. Les trois lectures ci-dessous rendent désormais `null`, et
// leurs appelants affichent « — » ou la cause servie.
// =============================================================================

/** Un nombre fini arrondi, ou `null` — jamais un 0 pour une absence. */
function montantServi(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur)
        ? Math.round(valeur)
        : null
}

/**
 * Économie FERME (registre **sûr**, hors CMDPS), telle que le moteur la sert
 * (`restitution.economie.ferme_eur`). `null` quand elle n'est pas servie : ce
 * n'est ni 0 ni un montant à déduire de deux autres champs.
 */
export function getEconomieFermeEur(etude: OptimTurpeV2Etude): number | null {
    return montantServi(etude.economie?.ferme_eur)
}

/** Économie NETTE servie (`restitution.economie.nette_eur`), ou `null`. */
export function getEconomieNetteEur(etude: OptimTurpeV2Etude): number | null {
    return montantServi(etude.economie?.nette_eur)
}

/**
 * CMDPS estimée du scénario retenu (€ HTVA, registre « à confirmer »).
 *
 * Deux champs SERVIS, dans cet ordre : le montant du bloc `economie`, puis le
 * coût de dépassement du scénario retenu (`restitution.couts.meilleure`). Aucun
 * des deux n'est servi → `null`, et l'écran dit l'absence. Arrondi à l'euro,
 * sauf une dépense non nulle sous 0,50 €, gardée telle que servie pour
 * s'écrire « moins de 1 € » et jamais « 0 € » (décision du pilote du
 * 14/10/2026).
 */
export function getCmdpsEstimeeEur(etude: OptimTurpeV2Etude): number | null {
    const servi = [
        etude.economie?.cmdps_estimee_eur,
        etude.meilleure?.couts?.turpeCMDPS,
    ].find((v) => typeof v === "number" && Number.isFinite(v))
    if (typeof servi !== "number") return null
    return servi !== 0 && Math.abs(servi) < 0.5 ? servi : Math.round(servi)
}

/**
 * L'état de l'étude, **lu** et non plus décidé ici (DEV-1913, DEV-1907).
 *
 * Le moteur calcule l'état sur la même grandeur que le chiffre qu'il sert : la
 * pastille et le montant ne peuvent plus se contredire (bloquants B4 et B5).
 * Cette fonction délègue à `lireChiffre`, qui lit le champ servi et, sur une
 * étude antérieure au lot, applique la règle publiée aux montants servis en le
 * disant. Elle rend `null` quand il n'y a pas d'état : une absence n'est ni un 0
 * ni une « structure déjà optimale » (constat F3.15).
 */
export function resolveOptimTurpeV2State(
    etude: OptimTurpeV2Etude
): OptimTurpeV2EtatServi | null {
    return lireChiffre(etude).etat
}

/**
 * Réserves explicites (« valide sous réserve(s) ») — listées et assumées par
 * l'utilisateur. Pas de jugement « fiabilité faible ».
 */
/**
 * PP1 / Obligation de Capacité : détail réglementaire jugé non pertinent à afficher ici
 * (Mathieu, DEV-783). Filtré quel que soit le canal — `reserves` (courtage) ou `warnings`.
 */
function isPp1Reserve(text: string): boolean {
    return /pp1|obligation.{0,5}de.{0,5}capacit/i.test(text)
}

export function getReserves(etude: OptimTurpeV2Etude): OptimTurpeV2Reserve[] {
    const reserves: OptimTurpeV2Reserve[] = []
    // Réserves précalculées par courtage (bloc `restitution.reserves`, DEV-762) — ex. données
    // extrapolées. Canal fiable (la façade api-metier ne propage pas l'enveloppe `warnings`).
    // La suggestion de meilleure période n'est PAS une réserve sur le résultat : c'est une action
    // (cf. getMeilleurePeriode → bouton « relancer ») → on l'exclut de la liste des réserves.
    // Le fait d'injection est écrit sur la fiche du compteur (DEV-2158) : la
    // réserve qui porte la même phrase ne le redit pas.
    const injection = phraseInjection(etude)
    for (const reserve of etude.reserves ?? []) {
        if ((reserve.code ?? "") === "meilleure-periode") continue
        if (
            injection &&
            reserve.label &&
            injection.startsWith(reserve.label.trim().replace(/\.$/, ""))
        ) {
            continue
        }
        if (isPp1Reserve(`${reserve.code ?? ""} ${reserve.label ?? ""}`))
            continue
        const code = reserve.code ?? "reserve"
        // Une même réserve servie deux fois (même code, même champ) ne s'affiche
        // qu'une fois (revue du 25/09, écart 5).
        if (
            reserves.some(
                (deja) =>
                    deja.code === code &&
                    (deja.champ ?? null) === (reserve.champ ?? null)
            )
        ) {
            continue
        }
        reserves.push({
            code,
            label: franciserLesNombres(
                reserve.label ?? reserve.code ?? "réserve"
            ),
            choisie: false,
            // Portée, champ et valeurs (§8.4, DEV-1910) : ce sont eux qui permettent
            // de rendre la réserve DANS la donnée concernée — sur la ligne de l'entrée,
            // à côté du montant, sous la recommandation — au lieu d'une liste en bas de
            // page où elle ne dit plus de quoi elle parle. Propagés tels quels, jamais
            // devinés : une réserve sans portée reste une réserve générale.
            ...(reserve.portee ? { portee: reserve.portee } : {}),
            ...(reserve.champ ? { champ: reserve.champ } : {}),
            ...(reserve.valeurs ? { valeurs: reserve.valeurs } : {}),
        })
    }
    // NB (DEV-783) : plus de réserve dérivée de `diagnostic.synthese.jours_manquants` — ce champ a
    // disparu du contrat (DEV-762/#117) et les trous sont désormais portés par `restitution.reserves`
    // (extrapolation tracée). La lire ici ne déclencherait plus rien et masquait la dérive de contrat.
    // Les avertissements que le moteur déclare DOUBLÉS par une réserve servie
    // (`code_avertissement`, courtage#249) : la réserve seule les dit.
    const doublesParUneReserve = new Set(
        (etude.reserves ?? [])
            .map((reserve) => reserve.code_avertissement)
            .filter((code): code is string => Boolean(code))
    )
    for (const warning of etude.warnings ?? []) {
        if ((warning.code ?? "").includes("donnee-aucune")) {
            continue
        }
        if (warning.code && doublesParUneReserve.has(warning.code)) continue
        if (isPp1Reserve(`${warning.code ?? ""} ${warning.message ?? ""}`))
            continue
        // Le même fait arrive souvent deux fois : en réserve détaillée du moteur
        // et en avertissement court (« Puissance réactive partiellement
        // absente », codes `reactive-completee` et `puissance-reactive-trouee`,
        // revue du 25/09, écart 5). On garde la réserve détaillée : un
        // avertissement dont le message ouvre déjà un libellé affiché, ou dont
        // le code est déjà servi, ne s'affiche pas une seconde fois.
        const message = (warning.message ?? "").trim()
        const code = warning.code ?? "warning"
        if (
            reserves.some(
                (deja) =>
                    deja.code === code ||
                    (message.length > 0 && deja.label.startsWith(message))
            )
        ) {
            continue
        }
        reserves.push({
            code,
            label: franciserLesNombres(
                warning.message ?? warning.code ?? "réserve"
            ),
            choisie: false,
        })
    }
    return reserves
}

/**
 * Réserves dont la portée n'est PAS une entrée : celles qui se rendent à côté du
 * montant ou de la recommandation, et celles que le moteur n'a pas qualifiées.
 *
 * Les réserves d'entrée, elles, sont rendues sur la ligne de leur champ dans le
 * registre (`reservesParChamp`) : les laisser ici les afficherait deux fois.
 */
export function getReservesHorsEntree(
    etude: OptimTurpeV2Etude
): OptimTurpeV2Reserve[] {
    return getReserves(etude).filter((reserve) => reserve.portee !== "entree")
}

/**
 * Niveau de qualité de la COURBE DE CHARGE (DEV-762, libellés revus §8.5).
 * `undefined` si l'étude ne porte pas le bloc qualité (documents anciens).
 *
 * Le code du niveau est inchangé ; ce sont ses LIBELLÉS qui changent, et le mot
 * « fiabilité faible » disparaît du module (DEV-848) : on décrit la donnée et ce
 * qu'on en fait, jamais la valeur de l'étude. Voir `LIBELLE_NIVEAU_DONNEES`.
 */
export function getNiveauDonnees(
    etude: OptimTurpeV2Etude
): OptimTurpeV2NiveauDonnees | undefined {
    return etude.qualite_donnees?.niveau
}

/**
 * Meilleure période disponible proposée à l'utilisateur (ré-exécutable) — DEV-762. On laisse le
 * client maître : on ne l'impose pas, on l'OFFRE. `null` s'il n'y a pas mieux que la période en cours.
 */
export function getMeilleurePeriode(
    etude: OptimTurpeV2Etude
): OptimTurpeV2MeilleurePeriode | null {
    return etude.qualite_donnees?.meilleure_periode ?? null
}

/**
 * Plancher de modulation RETENU par le moteur, lu sur `restitution.plancher_modulation`
 * (DEV-788), ou `undefined` quand le moteur ne le sert pas.
 *
 * Remplace `getSegmentFloorKva`, qui recopiait un plancher par segment (37 en C4,
 * 250 en C2). A6 supprime le minimum de C2 et confie la contrainte C4 au moteur :
 * la page n'annonce plus aucun plancher qu'elle aurait décidé, elle lit celui qui
 * a servi.
 */
export function getPlancherRetenu(
    etude: OptimTurpeV2Etude
): number | undefined {
    const defaut = etude.plancher_modulation?.segment_defaut
    return typeof defaut === "number" ? defaut : undefined
}

/**
 * Changement de FTA (formule tarifaire d'acheminement) entre l'actuelle et la
 * meilleure structure. Le moteur optimise la FTA **et** la structure de
 * puissance ensemble : la FTA recommandée peut différer de l'actuelle
 * (ex. C5 `BTINFMU4 → BTINFCU4`, C4 `BTSUPCU4 ↔ BTSUPLU4`). Doit être restitué
 * au même titre que le dénivelé.
 */
export function getFtaChange(etude: OptimTurpeV2Etude): {
    avant?: string
    apres?: string
    changed: boolean
} {
    const avant =
        etude.actuelle?.structure_tarifaire?.formule_tarifaire_acheminement?.fta
    const apres =
        etude.meilleure?.structure_tarifaire?.formule_tarifaire_acheminement
            ?.fta
    const changed =
        typeof avant === "string" &&
        typeof apres === "string" &&
        avant !== apres
    return { avant, apres, changed }
}

/**
 * Unité de puissance souscrite selon le segment : **C4/C5 = kVA** (puissance
 * apparente), **C2 = kW** (puissance active). On privilégie `diagnostic.grandeur`
 * (apparente/active) quand elle est présente, sinon on déduit du segment.
 */
export function getUnitePuissance(etude: OptimTurpeV2Etude): "kVA" | "kW" {
    const grandeur = etude.diagnostic?.grandeur
    if (grandeur === "active") return "kW"
    if (grandeur === "apparente") return "kVA"
    return etude.compteur?.segment === "C2" ? "kW" : "kVA"
}

/**
 * Découpe une série d'appels de puissance (DEV-789) en deux tracés MESURÉ vs EXTRAPOLÉ, pour les rendre
 * distinctement (ligne pleine vs pointillé gris). `comble[i] === 1` = point reconstruit (mesure absente
 * AVANT comblement) :
 * - `measuredY` = la puissance, coupée (`null`) sur les points comblés → la ligne mesurée se rompt sur
 *   les trous (avec `connectgaps: false`) ;
 * - `extrapY` = la puissance sur les points comblés ET les points-frontière mesurés adjacents — ces
 *   bornes RACCORDENT le segment pointillé à la ligne mesurée (pas de saut visuel) ;
 * - `hasCompleted` = au moins un point extrapolé (sinon pas de 2ᵉ tracé ni de légende).
 *
 * Sans masque (`comble` vide / absent), tout est mesuré : `measuredY === kw`, `extrapY` tout `null`.
 */
export function splitSerieTraces(
    kw: number[],
    comble: number[] = []
): {
    measuredY: (number | null)[]
    extrapY: (number | null)[]
    hasCompleted: boolean
} {
    const hasCompleted = comble.some((c) => c === 1)
    const measuredY: (number | null)[] = kw.map((v, i) =>
        comble[i] === 1 ? null : v
    )
    const extrapY: (number | null)[] = kw.map((v, i) =>
        comble[i] === 1 ? v : null
    )
    if (hasCompleted) {
        for (let i = 0; i < kw.length; i++) {
            if (comble[i] !== 1) continue
            if (i > 0) extrapY[i - 1] = kw[i - 1]
            if (i < kw.length - 1) extrapY[i + 1] = kw[i + 1]
        }
    }
    return { measuredY, extrapY, hasCompleted }
}

/** Ordre canonique des plages d'un dénivelé (PTE seulement en C2). */
export const PLAGE_ORDER: OptimTurpeV2PlageKey[] = [
    "PTE",
    "HPH",
    "HCH",
    "HPB",
    "HCB",
]

/** Plages présentes dans le dénivelé « meilleure » (ou « actuelle »), ordonnées. */
export function getPlagesDenivele(
    etude: OptimTurpeV2Etude
): OptimTurpeV2PlageKey[] {
    const den =
        etude.meilleure?.structure_tarifaire?.puissance_souscrite_soutirage
            ?.denivele ??
        etude.actuelle?.structure_tarifaire?.puissance_souscrite_soutirage
            ?.denivele ??
        {}
    return PLAGE_ORDER.filter((p) => typeof den[p] === "number")
}

/**
 * Plage qui dimensionne le dénivelé : `synthese.plage_pilote` si présent, sinon la plage au
 * dénivelé proposé le plus haut. C'est ELLE dont la forme du pic conseille l'arbitrage CMDPS
 * quand les montants ne tranchent pas (règle 4 du conseil v2 du 12/10/2026).
 */
export function getDrivingPlage(
    etude: OptimTurpeV2Etude
): OptimTurpeV2PlageKey | undefined {
    // La classe SERVIE par le moteur (`restitution.plage_motrice`, courtage#249)
    // est la seule source ; le diagnostic et le calcul local ne sont qu'un repli
    // pour une étude qui ne la porte pas.
    const motrice = etude.plage_motrice?.classe
    if (motrice && (PLAGE_ORDER as string[]).includes(motrice)) {
        return motrice as OptimTurpeV2PlageKey
    }
    const synthese = etude.diagnostic?.synthese
    const pilote = synthese?.plage_pilote
    if (pilote) return pilote
    // courtage#256 (DEV-2182, M11) : `null` dit une égalité sur les deux
    // critères. Aucune classe n'est alors désignée : le calcul local, qui
    // trancherait par l'ordre, ne la désigne pas davantage.
    if (pilote === null && (synthese?.plages_a_egalite?.length ?? 0) > 0) {
        return undefined
    }
    const plages = etude.diagnostic?.plages ?? {}
    let best: OptimTurpeV2PlageKey | undefined
    let bestVal = -Infinity
    for (const key of PLAGE_ORDER) {
        const v = plages[key]?.souscrit_propose_kva
        if (typeof v === "number" && v > bestVal) {
            bestVal = v
            best = key
        }
    }
    return best
}

/** Type de driver (pic isolé / comportement normal / saisonnier) de la plage dimensionnante. */
export function getDrivingDriverType(
    etude: OptimTurpeV2Etude
): OptimTurpeV2DriverType | undefined {
    const plage = getDrivingPlage(etude)
    return plage
        ? (etude.diagnostic?.plages?.[plage]?.driver?.type ?? undefined)
        : undefined
}

/**
 * Règle 4 du conseil v2 (12/10/2026), la dernière : elle ne décide que lorsque
 * les montants ne tranchent pas (`conseilDeRepli`, optim-turpe-v2.arbitrage.ts).
 * Scénario d'après la FORME du pic dimensionnant (décision Mathieu, DEV-783) :
 * - **pic isolé** (rare, court) → « avec CMDPS » : assumer le dépassement ponctuel coûte moins
 *   que de souscrire toute l'année pour le couvrir.
 * - **comportement normal / saisonnier** (récurrent) → « sans CMDPS » : la puissance revient,
 *   mieux vaut la couvrir que payer des dépassements répétés.
 */
export function recommendScenarioFromShape(
    etude: OptimTurpeV2Etude
): "sans" | "avec" {
    return getDrivingDriverType(etude) === "pic_isole" ? "avec" : "sans"
}

// =============================================================================
// Erreurs de service (DEV-1582) — un écran ne fabrique jamais une cause : il
// affiche la cause servie, ou dit qu'il ne la connaît pas.
//
// Revue adversariale 03/09 (CORRIGER) : le postulat initial (un `code` détaillé qualifié =
// une cause servie) était faux sur le chemin RÉEL de production. `ApiCourtageService` (api-metier)
// relaie l'erreur axios BRUTE de son appel au moteur courtage (`throw e`, jamais le
// `detail.message` du moteur), et le middleware d'erreur global construit la réponse à partir de
// `error.code`/`error.message` d'AXIOS — jamais du corps métier du moteur. Sur ce chemin,
// `detail.code` vaut par exemple `ERR_BAD_REQUEST`/`ECONNABORTED`, et `detail.message` vaut
// littéralement `Request failed with status code 400` ou `timeout of 120000ms exceeded` : un
// `code` non-`unknown` ne prouve donc RIEN — il faut aussi juger le CONTENU du message. Le défaut
// de fond (HomeysError qui écrase la cause amont) est côté api-metier, hors périmètre DEV-1582
// (ticketé à part) : ce module reste honnête SANS lui.
// =============================================================================

/** `handleServiceCall` pose ce texte quand la réponse ne porte aucun message exploitable. */
const GENERIC_SERVICE_CALL_MESSAGES = new Set([
    "Une erreur est survenue",
    "Une erreur inconnue est survenue",
])

/** Le message générique qu'axios fabrique lui-même — jamais une cause métier. */
const HTTP_JARGON_MESSAGE_RE = /^Request failed with status code \d+$/

/**
 * Codes AXIOS (pas métier) qu'un microservice amont (courtage, via `ApiCourtageService`) peut
 * laisser fuiter dans `detail.code` en relayant `error.code` sans le traduire. `unknown`/`UNKNOWN`
 * (posés par `handleServiceCall` quand la réponse ne porte pas de code, ou pour une erreur
 * réseau/CORS sans réponse, le cas H12) appartiennent à la même famille. Un message ACCOMPAGNANT
 * un de ces codes n'est jamais fiable, même s'il ne matche ni le générique ni le jargon HTTP
 * ci-dessus — cas mesuré : `ECONNABORTED` sert `timeout of 120000ms exceeded`, qui ne matche
 * aucun des deux filtres mais N'EST PAS une cause métier.
 *
 * Volontairement ABSENT d'ici : `unknown` (minuscule) — posé par `handleServiceCall` quand la
 * réponse ne porte simplement pas de `code`, ce qui n'invalide PAS un `message` par ailleurs
 * exploitable (ex. `{"detail":{"message":"Étude optimturpe introuvable"}}`, sans code — cause
 * servie qui mérite d'être affichée, pas remplacée par une panne inventée).
 */
const AXIOS_TECHNICAL_CODES = new Set([
    "UNKNOWN",
    "ERR_BAD_REQUEST",
    "ERR_BAD_RESPONSE",
    "ERR_NETWORK",
    "ECONNABORTED",
    "ETIMEDOUT",
])

/**
 * Un `ServiceError.message` n'est une cause SERVIE que s'il n'est ni le générique de
 * `handleServiceCall`, ni le jargon HTTP d'axios (`Request failed with status code …`), ni
 * IDENTIQUE à `technicalMessage` (l'invariant « jamais technicalMessage » tient sur la VALEUR,
 * pas seulement sur le champ — `HomeysError` d'api-metier blanchit le message technique dans
 * `detail.message`, donc `message === technicalMessage` en pratique sur ce chemin), et que son
 * `code` n'est pas un code technique axios qui a fuité (`AXIOS_TECHNICAL_CODES`).
 */
function isServedMessage(se: ServiceError): boolean {
    const msg = se.message
    if (!msg) return false
    if (GENERIC_SERVICE_CALL_MESSAGES.has(msg)) return false
    if (HTTP_JARGON_MESSAGE_RE.test(msg)) return false
    if (se.technicalMessage && msg === se.technicalMessage) return false
    if (AXIOS_TECHNICAL_CODES.has(se.code)) return false
    return true
}

/**
 * L'erreur est-elle TECHNIQUE, sans cause nommée (DEV-2311, constat du 07/10/2026) ? Un code
 * axios qui a fuité (`AXIOS_TECHNICAL_CODES`, ou tout code `ERR_…`) ou le jargon HTTP d'axios
 * (« Request failed with status code 422 ») ne sont pas une cause du catalogue du moteur :
 * l'écran ne les montre jamais au client comme un refus de son étude.
 */
export function estErreurTechnique(error: unknown): boolean {
    if (!(error instanceof ServiceError)) return false
    const code = typeof error.code === "string" ? error.code : ""
    if (AXIOS_TECHNICAL_CODES.has(code) || code.startsWith("ERR_")) return true
    return /status code/i.test(error.message ?? "")
}

/**
 * Traduit une erreur en message métier lisible, sans jamais fabriquer une cause que le serveur
 * n'a pas dite (DEV-762, DEV-848 item 2, DEV-1457, DEV-1582). `handleServiceCall`
 * (`services/helpers.ts`) transforme une erreur AVEC réponse en `ServiceError` — sauf
 * `axios.isCancel(error)`, qu'il relance TEL QUEL (l'objet cancel axios brut, sans `.response`
 * ni aucun champ `ServiceError`) : une requête annulée n'est pas une `ServiceError` et doit être
 * écartée AVANT d'appeler cette fonction (voir la garde dans `applyRunError`/`downloadReport`).
 *
 * `description` est l'explication rédigée POUR l'utilisateur par l'API (`detail.description`,
 * DEV-1088) — jamais `technicalMessage` (le jargon axios, qui ne doit JAMAIS être affiché et
 * n'est donc jamais lu ici).
 *
 * - `backendMsg` = ce que le serveur a effectivement dit : `description` en priorité, sinon
 *   `message` SI `isServedMessage` le juge exploitable (cf. ci-dessus — un `code` qualifié ne
 *   suffit plus à lui seul, DEV-1582 revue 03/09).
 * - `backendMsg` défini → affiché tel quel.
 * - sinon, `status` ABSENT ou `≥ 500` → panne technique probable de notre côté →
 *   `fallbacks.surPanne`. Le `code` ne pilote PLUS ce choix (un 4xx sans cause exploitable
 *   n'est pas une panne de notre côté : « défaut de notre côté, réessayez » y serait un mauvais
 *   conseil ET une cause inventée).
 * - sinon → `fallbacks.surAutre(status)`, CHOISI selon le statut HTTP (pas un code technique
 *   axios du type `ERR_BAD_REQUEST`, qui n'apprend rien à personne). Revue d'ouverture du
 *   12/10/2026 (C9) : le statut ne s'écrit JAMAIS dans la phrase du client (« erreur 403 »
 *   n'apprend rien à personne) ; la phrase dit quoi faire (`consigneApresRefus`) et le statut
 *   reste au suivi, écrit à la console avec le code servi.
 */
export function resolveServiceErrorMessage(
    error: unknown,
    fallbacks: { surPanne: string; surAutre: (status: number) => string }
): string {
    const se = error instanceof ServiceError ? error : null
    const backendMsg =
        getUserFacingDescription(error) ??
        (se && isServedMessage(se) ? se.message : undefined)
    if (backendMsg) return backendMsg
    const status = se?.status
    if (status === null || status === undefined || status >= 500) {
        return fallbacks.surPanne
    }
    // Le statut et le code restent au suivi, jamais à l'écran (C9).
    console.warn("[Optim TURPE] refus sans cause servie", {
        status,
        code: se?.code ?? null,
    })
    return fallbacks.surAutre(status)
}

/**
 * Ce que le client peut faire après un refus sans cause servie (revue d'ouverture du
 * 12/10/2026, C9) : une consigne, jamais le statut HTTP. Un refus de droits renvoie vers
 * l'interlocuteur habituel, qui peut les vérifier ; un excès de demandes invite à patienter ;
 * le reste à recharger la page puis réessayer.
 */
export function consigneApresRefus(status: number): string {
    if (status === 401 || status === 403) {
        return "Votre compte n'a peut-être pas les droits nécessaires sur ce compteur : votre interlocuteur habituel pourra les vérifier."
    }
    if (status === 429) {
        return "Trop de demandes en peu de temps : patientez une minute, puis réessayez."
    }
    return "Rechargez la page puis réessayez ; si cela se reproduit, signalez-le à votre interlocuteur habituel."
}

// =============================================================================
// Revue des libellés du 25/09/2026 (DEV-2164, DEV-1892 écarts 5 à 13).
//
// Ce que le client doit vivre : des nombres écrits à la française, une seule
// classe dimensionnante d'un bout à l'autre de la page, des phrases de lecture
// qui disent la situation réelle du compteur, et rien sur les dépassements de
// puissance là où ils n'existent pas (C5).
// =============================================================================

/**
 * Un nombre écrit à la française : virgule décimale, espace insécable (U+00A0) des
 * milliers (« 1 353,6 », « 8,06 »). `null` pour une absence, jamais « 0 ».
 */
export function formatNombreFr(
    valeur: number | null | undefined,
    decimalesMax = 2
): string | null {
    if (typeof valeur !== "number" || !Number.isFinite(valeur)) return null
    return formateurFr({
        maximumFractionDigits: decimalesMax,
    }).format(valeur)
}

/** Une puissance avec son unité, séparée par une espace insécable (« 108 kVA »), ou « — ». */
export function formatPuissanceFr(
    valeur: number | null | undefined,
    unite: string | null | undefined,
    decimalesMax = 2
): string {
    const nombre = formatNombreFr(valeur, decimalesMax)
    if (nombre === null) return "—"
    return unite ? `${nombre}\u00a0${unite}` : nombre
}

/** Un repère vertical de la répartition (monotone) : sa valeur et le côté de son étiquette. */
export type RepereDeLaRepartition = {
    cle: "propose" | "avant"
    x: number
    /** `right` : l'étiquette s'écrit à gauche du trait ; `left` : à droite. */
    ancre: "left" | "right"
}

/**
 * Le cadre de la répartition et ses repères (revue témoin du 15/10/2026).
 * D9 : l'axe couvre la courbe ET les deux repères, avec 8 % de marge à
 * droite ; un trait « recommandé » plus haut que la courbe ne sort plus du
 * cadre. D10 : les étiquettes s'ancrent selon l'ordre des valeurs, la plus
 * basse écrite à gauche de son trait, la plus haute à droite, pour ne jamais
 * se superposer.
 */
export function reperesDeLaRepartition(
    xMaxCourbe: number,
    propose: number | null | undefined,
    avant: number | null | undefined
): { xMax: number; borneAxe: number; reperes: RepereDeLaRepartition[] } {
    const valeurs: RepereDeLaRepartition[] = []
    if (typeof propose === "number" && Number.isFinite(propose))
        valeurs.push({ cle: "propose", x: propose, ancre: "left" })
    if (typeof avant === "number" && Number.isFinite(avant))
        valeurs.push({ cle: "avant", x: avant, ancre: "left" })
    const xMax = Math.max(xMaxCourbe, ...valeurs.map((v) => v.x))
    const borneAxe = xMax * 1.08
    if (valeurs.length === 2) {
        const [a, b] = valeurs
        // À valeur égale, « recommandé » à gauche, « aujourd'hui » à droite.
        const basse = a.x <= b.x ? a : b
        basse.ancre = "right"
    } else if (valeurs.length === 1) {
        // Seul et proche du bord droit : écrit vers l'intérieur du cadre.
        valeurs[0].ancre = valeurs[0].x > 0.6 * borneAxe ? "right" : "left"
    }
    return { xMax, borneAxe, reperes: valeurs }
}

/**
 * Revue du 19/10/2026 (N6) : sur la courbe des appels de puissance (10 min),
 * les étiquettes des deux lignes horizontales (« Puissance souscrite
 * actuelle », « … recommandée ») se chevauchaient quand les valeurs étaient
 * proches (48 et 46 kVA). La plus haute s'écrit au-dessus de sa ligne, la
 * plus basse au-dessous : elles ne se touchent jamais, quel que soit l'écart.
 * À valeur égale, « actuelle » au-dessus, « recommandée » au-dessous.
 */
export function ancresDesLignesHorizontales(
    propose: number | null | undefined,
    avant: number | null | undefined
): { propose: "top" | "bottom"; avant: "top" | "bottom" } {
    const p = typeof propose === "number" && Number.isFinite(propose)
    const a = typeof avant === "number" && Number.isFinite(avant)
    if (!p || !a) return { propose: "bottom", avant: "bottom" }
    return (propose as number) > (avant as number)
        ? { propose: "bottom", avant: "top" }
        : { propose: "top", avant: "bottom" }
}

const NOMS_DES_MOIS = [
    "janvier",
    "février",
    "mars",
    "avril",
    "mai",
    "juin",
    "juillet",
    "août",
    "septembre",
    "octobre",
    "novembre",
    "décembre",
]

/** Des numéros de mois (1 à 12) écrits en toutes lettres : « avril, mai, juin ». */
export function nomsDesMois(mois: number[] | null | undefined): string {
    return (mois ?? [])
        .map((m) =>
            Number.isInteger(m) && m >= 1 && m <= 12
                ? NOMS_DES_MOIS[m - 1]
                : null
        )
        .filter((nom): nom is string => nom !== null)
        .join(", ")
}

/**
 * Un horodatage ISO écrit JJ/MM/AAAA HH:MM à l'heure de Paris. Rend la valeur
 * telle quelle si elle ne se lit pas comme une date, et « — » pour une absence.
 *
 * Fourni pour la colonne « Calculée le » de l'historique des études, qui
 * affichait l'ISO brut (`2026-09-25T12:55:39.785684+00:00`).
 */
export function formatDateHeureParis(iso: string | null | undefined): string {
    if (!iso) return "—"
    // La forme de la base sépare la date et l'heure par une espace.
    let normalise = iso.trim().replace(" ", "T")
    // `Date.parse` ne lit pas les microsecondes : on les ramène aux millisecondes.
    normalise = normalise.replace(/(\.\d{3})\d+/, "$1")
    // Une date-heure ISO sans « Z » ni offset est lue par `Date.parse` à l'heure
    // LOCALE du navigateur (ECMA-262). Les horodatages servis sont en UTC : on
    // le dit explicitement, pour que l'heure de Paris soit la même partout.
    if (
        /T\d{2}:\d{2}/.test(normalise) &&
        !/(Z|[+-]\d{2}:?\d{2})$/i.test(normalise)
    ) {
        normalise = `${normalise}Z`
    }
    const ms = Date.parse(normalise)
    // Une date illisible n'est pas recopiée telle quelle : c'est une absence.
    if (Number.isNaN(ms)) return "—"
    const parties = new Intl.DateTimeFormat("fr-FR", {
        timeZone: "Europe/Paris",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).formatToParts(new Date(ms))
    const partie = (type: Intl.DateTimeFormatPartTypes) =>
        parties.find((p) => p.type === type)?.value ?? ""
    return `${partie("day")}/${partie("month")}/${partie("year")} à ${partie("hour")}:${partie("minute")}`
}

/**
 * Une date du jour (`2026-08-01`, ou le jour d'un horodatage) écrite à la
 * française, `JJ/MM/AAAA`. `null` pour une absence ou une forme illisible.
 */
export function formatDateFr(iso: string | null | undefined): string | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso ?? "").trim())
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null
}

/**
 * Réécrit à la française les nombres décimaux d'un libellé servi par le moteur
 * (« 8.06 kVA » → « 8,06 kVA »). Ne touche ni aux dates, ni aux suites à plus
 * d'un point (numéros de version), ni aux entiers.
 */
export function franciserLesNombres(texte: string): string {
    return texte.replace(
        /(?<![\d.,])(\d+)\.(\d+)(?![\d.])/g,
        (_tout, entier: string, decimales: string) =>
            `${formatNombreFr(Number(entier), 0)},${decimales}`
    )
}

/**
 * Le régime fiscal du montant, écrit comme partout ailleurs sur la page :
 * « € HT ». Le moteur sert `HTVA` ; un régime inconnu reste dit tel quel.
 */
export function libelleRegimeFiscal(regime: string | null | undefined): string {
    if (!regime || /^(HT|HTVA|hors ?taxes?)$/i.test(regime.trim())) {
        return "€ HT"
    }
    return `€ ${regime}`
}

/** Le segment est-il un C5 (puissance unique, aucune composante de dépassement) ? */
export function estC5(etude: OptimTurpeV2Etude | null | undefined): boolean {
    return etude?.compteur?.segment === "C5"
}

/** Les qualifications d'injection qui appellent une phrase sur CE compteur. */
const INJECTION_A_DIRE = new Set(["producteur_pur", "autoconsommation"])

/**
 * La phrase sur l'injection de CE compteur (`restitution.injection`, DEV-2158),
 * ou `null`. Elle n'est écrite que si la qualification servie le dit : le
 * catalogue du périmètre, lu comme un fait, l'imprimait sous un C4 qui soutire.
 */
export function phraseInjection(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    const injection = etude?.injection
    const qualification = injection?.qualification ?? null
    const libelle = (injection?.libelle ?? "").trim()
    if (!qualification || !INJECTION_A_DIRE.has(qualification) || !libelle) {
        return null
    }
    return libelle.endsWith(".") ? libelle : `${libelle}.`
}

/**
 * La CMDPS a-t-elle un objet sur cette étude ? Le moteur le dit
 * (`restitution.cmdps_applicable`, courtage#249) ; une étude qui ne le porte pas
 * se lit sur son segment (un C5 n'a pas de CMDPS, décision du 25/09).
 */
export function cmdpsApplicable(
    etude: OptimTurpeV2Etude | null | undefined
): boolean {
    if (typeof etude?.cmdps_applicable === "boolean") {
        return etude.cmdps_applicable
    }
    return !estC5(etude)
}

/**
 * Le point est-il triphasé ? Seul ce que l'étude SERT le dit : le branchement
 * retenu par le moteur (`restitution.branchement.valeur`), l'entrée saisie, ou
 * une réserve qui n'existe que sur un triphasé. Une absence ne prouve jamais un
 * monophasé : elle rend `false` et le champ par phase ne s'affiche pas.
 */
export function estTriphase(
    etude: OptimTurpeV2Etude | null | undefined
): boolean {
    if (!etude) return false
    if (etude.branchement?.valeur === "triphase") return true
    if (etude.inputs?.compteur?.raccordement?.branchement?.triphase === true) {
        return true
    }
    return (etude.reserves ?? []).some((r) =>
        [
            "maxima-par-phase-non-disponibles",
            "branchement-triphase-desequilibre-non-mesure",
        ].includes(r.code ?? "")
    )
}

/** Les quatre classes du dénivelé d'un C4 (formules `BTSUP…`), sans pointe. */
const CLASSES_BT_SUP_36 = ["HPH", "HCH", "HPB", "HCB"]

/** Les cinq classes d'une formule HTA à pointe fixe (`HTACU5`, `HTALU5`). */
const CLASSES_HTA_POINTE_FIXE = ["PTE", "HPH", "HPB", "HCH", "HCB"]

/**
 * Les classes temporelles que porte une formule tarifaire, ou `null` quand la
 * formule ne permet pas de le dire (on n'écarte alors aucune ligne).
 *
 * Une formule `BTSUP…` (C4) n'a ni pointe fixe ni pointe mobile : une ligne
 * « PM » ou « PTE » servie sur un tel compteur ne décrit aucune classe du client.
 */
export function classesDeLaFta(
    fta: string | null | undefined
): string[] | null {
    if (!fta) return null
    if (/^BTSUP/i.test(fta)) return CLASSES_BT_SUP_36
    // Les deux formules HTA que le pricer connaît (`get_classes_fta`) : cinq
    // classes, dont la pointe fixe. Une formule qu'il ne connaît pas (pointe
    // mobile) reste « on ne sait pas ».
    if (/^HTA(CU|LU)5$/i.test(fta)) return CLASSES_HTA_POINTE_FIXE
    return null
}

/**
 * La formule a-t-elle une classe de POINTE ? Même règle que le moteur
 * (`data_quality.fta_a_classe_de_pointe`, courtage#256, DEV-2186) : `null`
 * quand la formule ne permet pas de le dire.
 */
export function ftaAClasseDePointe(
    fta: string | null | undefined
): boolean | null {
    const classes = classesDeLaFta(fta)
    if (!classes) return null
    return classes.some((classe) => classe === "PTE" || classe === "PM")
}

/**
 * La classe dimensionnante de l'étude, UNE seule vérité pour toute la page :
 * la plage pilote servie par le moteur (`diagnostic.synthese.plage_pilote`),
 * sinon la première plage au dénivelé proposé le plus haut, dans l'ordre
 * canonique. L'en-tête, la preuve et les cartes de scénario la lisent ici.
 */
export function getClasseDimensionnante(
    etude: OptimTurpeV2Etude | null | undefined
): OptimTurpeV2PlageKey | null {
    return etude ? (getDrivingPlage(etude) ?? null) : null
}

/**
 * La phrase de lecture sous la répartition des appels de puissance, conditionnée
 * à la situation réelle du souscrit actuel devant la pointe mesurée.
 *
 * Trois situations, jamais un gabarit : le souscrit est au-dessus de tout ce qui
 * est mesuré, il est égal à la pointe, ou il est dépassé (et de combien).
 */
export function lectureRepartition(
    p: {
        proposePct: number | null
        propose: number | null
        avant: number | null
        avantPct: number | null
        pointe: number | null
        unite: string
    },
    structureChoisie = false
): string {
    if (p.proposePct === null || p.propose === null) return ""
    const debut = `Lecture : la puissance souscrite ${structureChoisie ? "saisie" : "recommandée"} (${formatPuissanceFr(p.propose, p.unite)}) couvre ${formatNombreFr(p.proposePct, 0)} % du temps`
    if (p.avant === null) return `${debut}.`
    const avant = formatPuissanceFr(p.avant, p.unite)
    if (p.pointe === null) return `${debut}.`
    if (p.avant > p.pointe) {
        return `${debut} ; la puissance souscrite actuelle (${avant}) est au-dessus de tout ce qui est mesuré.`
    }
    if (p.avant === p.pointe) {
        return `${debut} ; la puissance souscrite actuelle (${avant}) est égale à la puissance maximale atteinte.`
    }
    const ecart = formatPuissanceFr(p.pointe - p.avant, p.unite)
    const pointe = formatPuissanceFr(p.pointe, p.unite)
    const tempsAuDessus =
        p.avantPct !== null && p.avantPct < 100
            ? `, sur environ ${formatNombreFr(Math.max(100 - p.avantPct, 1), 0)} % du temps`
            : ""
    return `${debut} ; la puissance souscrite actuelle (${avant}) est dépassée : la puissance maximale atteinte est de ${pointe}, soit ${ecart} au-dessus${tempsAuDessus}.`
}

/**
 * Le niveau de qualité, dit sans contredire la couverture.
 *
 * Une courbe couverte à 100 % n'est jamais « partielle » : quand le niveau est
 * plafonné alors que la courbe est complète, c'est la PÉRIODE qui le plafonne
 * (fenêtre plus courte qu'une année, Q-A), et c'est elle que l'on nomme.
 */
export function libelleNiveauCourbe(etude: OptimTurpeV2Etude): string | null {
    const niveau = etude.qualite_donnees?.niveau
    if (!niveau) return null
    const couverture = etude.qualite_donnees?.couverture_mesuree_pct
    const complete = typeof couverture === "number" && couverture >= 99.5
    if (!complete || niveau === "fiable" || niveau === "faible") {
        return LIBELLE_NIVEAU_DONNEES_REF[niveau]
    }
    const jours = lireChiffre(etude).periode.jours
    if (jours !== null && jours < 365) {
        return "Période plus courte qu'une année : recommandation donnée sous réserve"
    }
    return "Recommandation donnée sous réserve"
}

/**
 * La longueur de la fenêtre analysée, avec ce qu'elle retire : « 89 jours, sous
 * l'année : pas d'équivalent annuel ». `null` quand la durée n'est pas connue.
 */
export function libelleDureeFenetre(etude: OptimTurpeV2Etude): string | null {
    const jours = lireChiffre(etude).periode.jours
    if (jours === null) return null
    const duree = `${formatNombreFr(jours, 0)} jour${jours > 1 ? "s" : ""}`
    if (jours < JOURS_EQUIVALENT_ANNUEL) {
        return `${duree}, sous l'année : pas d'équivalent annuel`
    }
    return duree
}

// =============================================================================
// La décomposition TURPE réconciliée (DEV-2168).
//
// Ce que le client doit vivre : les lignes du tableau se somment au chiffre de
// tête. Le tableau n'avait que CSF, CSV, CMDPS et CER, sans CTA ni total : sur
// le C4 interne du 25/09, 253 € de lignes pour +296 € en tête. Le moteur sert
// la réconciliation poste par poste (`economie.reconciliation`) : on la rend
// telle quelle, sans rien recalculer.
// =============================================================================

/** Le nom en clair de chaque poste : le dictionnaire client (DEV-2218). */
export { LIBELLE_POSTE_TURPE }

export type LigneDecompositionTurpe = {
    /** `poste` : une composante ; `total` : le total ferme ; `hors-total` : la CMDPS ; `nette` : le chiffre de tête. */
    nature: "poste" | "total" | "hors-total" | "nette"
    libelle: string
    avantEur: number | null
    apresEur: number | null
    /** Économie sur ce poste (positive = moins cher après), servie par le moteur. */
    economieEur: number | null
}

export type DecompositionTurpe = {
    /** Vrai quand la réconciliation du moteur est servie et rendue. */
    reconciliee: boolean
    lignes: LigneDecompositionTurpe[]
}

function eurServi(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/**
 * La décomposition TURPE à afficher. Avec la réconciliation servie : un poste
 * par ligne (avant, après, économie), le total ferme, la CMDPS hors total, puis
 * l'économie nette, égale au chiffre de tête. Sans elle (étude ancienne) : les
 * quatre composantes historiques, sans total, comme avant.
 *
 * Sur un C5, ni CMDPS ni CER : la CMDPS n'existe pas (décision du 25/09,
 * DEV-2164 cas 11), et la ligne CER n'y porte rien. Par prudence, une CER
 * NON nulle servie sur un C5 reste affichée : l'écarter romprait la somme.
 */
export function lireDecompositionTurpe(
    etude: OptimTurpeV2Etude
): DecompositionTurpe {
    const c5 = !cmdpsApplicable(etude)
    const av = (etude.actuelle?.couts ?? {}) as Record<string, unknown>
    const ap = (etude.meilleure?.couts ?? {}) as Record<string, unknown>
    const cerNulle = !eurServi(av.turpeCER) && !eurServi(ap.turpeCER)
    const exclus = c5 ? ["turpeCMDPS", ...(cerNulle ? ["turpeCER"] : [])] : []
    const reconciliation = etude.economie?.reconciliation
    const postes = reconciliation?.postes ?? []

    if (postes.length === 0) {
        const lignes = ["turpeCSF", "turpeCSV", "turpeCMDPS", "turpeCER"]
            .filter((poste) => !exclus.includes(poste))
            .map<LigneDecompositionTurpe>((poste) => ({
                nature: "poste",
                libelle: LIBELLE_POSTE_TURPE[poste],
                avantEur: eurServi(av[poste]),
                apresEur: eurServi(ap[poste]),
                economieEur: null,
            }))
        return { reconciliee: false, lignes }
    }

    const lignes: LigneDecompositionTurpe[] = postes
        .filter((p) => typeof p.poste === "string" && !exclus.includes(p.poste))
        .map((p) => ({
            nature: "poste",
            libelle: LIBELLE_POSTE_TURPE[p.poste as string] ?? String(p.poste),
            avantEur: eurServi(av[p.poste as string]),
            apresEur: eurServi(ap[p.poste as string]),
            economieEur: eurServi(p.economie_eur),
        }))
    lignes.push({
        nature: "total",
        // Revue du support du 08/10 (A3) : « total de la part » laissait
        // chercher une autre partie ; ce total est celui d'avant les
        // dépassements, que la ligne suivante dit « hors de ce total ».
        libelle: LIBELLE_TOTAL_HORS_DEPASSEMENTS,
        avantEur: null,
        apresEur: null,
        economieEur: eurServi(reconciliation?.total_ferme_eur),
    })
    if (!c5) {
        const horsTotal = reconciliation?.cmdps_hors_total
        const economieCmdps =
            typeof horsTotal === "object" && horsTotal !== null
                ? eurServi(horsTotal.economie_eur)
                : null
        lignes.push({
            nature: "hors-total",
            libelle: `${LIBELLE_DEPASSEMENTS_ESTIMES_SIGLE}, hors de ce total`,
            avantEur: eurServi(av.turpeCMDPS),
            apresEur: eurServi(ap.turpeCMDPS),
            economieEur: economieCmdps,
        })
        lignes.push({
            nature: "nette",
            libelle: "Économie nette de la période",
            avantEur: null,
            apresEur: null,
            economieEur: eurServi(etude.economie?.nette_eur),
        })
    }
    return { reconciliee: true, lignes }
}
